import {
    CssSelection,
    CssText,
    CssSentence,
    CssWord,
    CssObject,
    ContentCssClass
} from "../../content/content-view.objects";
import {Subject} from "rxjs";
import {Injectable} from "@angular/core";

/**
 * This represents what granularity of selection the user is allowed to make in the content
 */
export enum AllowedSelectionType {
    WORD,
    SENTENCE,
    TEXT,
    NONE
}

@Injectable()
export class ContentCursor {

    get wordChanged(): Subject<CssWord> {
        return this._wordChanged;
    }

    get sentenceChanged(): Subject<CssSentence> {
        return this._sentenceChanged;
    }

    get textChanged(): Subject<CssText> {
        return this._textChanged;
    }

    get selectedWord(): CssWord {
        return this._selectedWord;
    }

    get selectedSentence(): CssSentence {
        return this._selectedSentence;
    }

    get selectedText(): CssText {
        return this._selectedText;
    }

    get selection(): CssSelection {
        return this._selection;
    }

    get firstText(): CssText {
        return this.selection && this.selection.firstNonImageChild() ? this.selection.firstNonImageChild() : undefined;
    }

    get firstSentence(): CssSentence {
        let text:CssText = this.firstText;
        return text && text.firstChild() ? text.firstChild() : undefined;
    }

    get firstWord(): CssWord {
        let sentence:CssSentence = this.firstSentence;
        return sentence && sentence.firstChild() ? sentence.firstChild() : undefined;
    }

    private allowedSelectionType: AllowedSelectionType = AllowedSelectionType.WORD;

    private _wordChanged: Subject<CssWord> = new Subject<CssWord>();
    private _sentenceChanged: Subject<CssSentence> = new Subject<CssSentence>();
    private _textChanged: Subject<CssText> = new Subject<CssText>();

    private _selectedGlossaryWord: CssWord;
    private _selectedWord: CssWord;
    private _selectedSentence: CssSentence;
    private _selectedText: CssText;

    private _selection: CssSelection;
    private viewMode: ContentCssClass;

    private SELECTED_WORD: string = "_selectedWord";
    private SELECTED_SENTENCE: string = "_selectedSentence";
    private SELECTED_TEXT: string = "_selectedText";

    constructor() {}

    public init(selection: CssSelection): void {
        this._selection = selection;
    }

    public goNextWord(): void {
        let newSelection = this._selection.nextWord(this._selectedWord);
        this.selectWord(newSelection);
    }

    public goNextSentence(): void {
        let newSelection = this._selection.nextSentence(this._selectedSentence);
        this.selectSentence(newSelection);
    }

    public goNextText(): void {
        let newSelection = this._selection.nextText(this._selectedText);
        this.selectText(newSelection);
    }

    public selectText(text: CssText, userSelected?: boolean): void {
        this.switchSelection(text, this.SELECTED_TEXT, userSelected);
    }

    public selectSentence(sentence: CssSentence, userSelected?: boolean): void {
        this.switchSelection(sentence, this.SELECTED_SENTENCE, userSelected);
        let parent = sentence ? sentence.parentText() : undefined;
        if (this._selectedText !== parent) {
            this.selectText(parent, userSelected);
        }
    }

    public selectWord(word: CssWord, userSelected?: boolean): void {
        this.deselectGlossaryPhrase();
        this.switchSelection(word, this.SELECTED_WORD, userSelected);
        let parent = word ? word.parentSentence() : undefined;
        if (this._selectedSentence !== parent) {
            this.selectSentence(parent, userSelected);
        }
    }

    public selectGlossaryPhrase(word: CssWord): void {
        this._selectedGlossaryWord = word;
        word.selectGlossaryPhrase(true);
    }

    public deselectGlossaryPhrase(): void {
        if (this._selectedGlossaryWord) {
            this._selectedGlossaryWord.selectGlossaryPhrase(false);
        }
        this._selectedGlossaryWord = undefined;
    }

    public startAtWord(word: CssWord): void {
        this._selectedWord = word;
        word.select();
        if (word.parentSentence()) {
            this.selectSentence(word.parentSentence());
        }
    }

    /**
     * Used for the mouse/touch input.
     * It is filtered by the currently allowed selection type
     * @param word - word the user clicked
     */
    public userSelectWord(word: CssWord): void {
        if (this.allowedSelectionType === AllowedSelectionType.WORD) {
            this.selectWord(word, true);
        } else if (this.allowedSelectionType === AllowedSelectionType.SENTENCE) {
            this.selectSentence(word.parentSentence(), true);
        } else if (this.allowedSelectionType === AllowedSelectionType.TEXT) {
            this.selectText(word.parentText(), true);
        }
    }

    public clearSelection(): void {
        this.selectWord(undefined);
    }

    public setMode(mode: ContentCssClass): void {
        this._selection.removeCssClass(this.viewMode);
        this.viewMode = mode;
        this._selection.addCssClass(this.viewMode);
        console.log("Set viewmode to :" + mode);
    }

    public getMode(): ContentCssClass {
        return this.viewMode;
    }

    public setAllowedSelectionType(type: AllowedSelectionType): void {
        this.allowedSelectionType = type;
        if (this.allowedSelectionType > AllowedSelectionType.WORD) {
            this.switchSelection(undefined, this.SELECTED_WORD);
        }
        if (this.allowedSelectionType > AllowedSelectionType.SENTENCE) {
            this.switchSelection(undefined, this.SELECTED_SENTENCE);
        }
        if (this.allowedSelectionType > AllowedSelectionType.TEXT) {
            this.switchSelection(undefined, this.SELECTED_TEXT);
        }
    }

    public getAllowedSelectionType(): AllowedSelectionType {
        return this.allowedSelectionType;
    }

    private switchSelection(object: CssWord|CssSentence|CssText, propName: string, userSelected?: boolean): boolean {
        let oldSelection = <CssWord|CssSentence|CssText>this[propName];

        if (oldSelection) {
            oldSelection.select(false);
        }
        if (object) {
            object.select();
            object.userSelected = !!userSelected;
            this[propName] = object;
            this.emitSelectionChange(object);
            object.userSelected = false;
        } else {
            this[propName] = undefined;
        }
        return true; // selection changed or was removed
    }

    private emitSelectionChange(object: CssObject): void {
        if (object instanceof CssWord) {
            this._wordChanged.next(object);
        } else if (object instanceof CssSentence) {
            this._sentenceChanged.next(object);
        } else if (object instanceof CssText) {
            this._textChanged.next(object);
        }
    }
}
